/**
 * Continuing Review form schema.
 *
 * Rendered by the same FormSubmissionFields renderer as the initial submission
 * form, but as a separate, much shorter schema focused on study progress,
 * safety, and renewal documentation.
 *
 * Field-name contract (do not rename — shared services depend on these keys):
 *   protocol_title, ref_name, sponsor_name  -> Stripe invoice description + doc filenames
 *   billing_contact_company                 -> Stripe customer name (falls back to
 *                                              sponsor_name in stripe_invoice_creation)
 *   invoice_email, billing_contact_first_name, billing_contact_last_name,
 *   billing_contact_phone, billing_contact_company
 *                                           -> payments row upsert in autosaveSubmission
 *   pi_email                                -> mirrored to submissions.pi_email (PI approval flow)
 *   doc_*                                   -> documents.type values
 *
 * cr_-prefixed names are continuing-review-only answers stored in
 * submissions.data like every other form field.
 */

import { FormSection } from '../components/FormSubmissionFields';

const YES_NO = ['Yes', 'No'];

const NUMERIC_VALIDATION = {
  required: true,
  maxLength: 7,
  pattern: /^[0-9]+$/,
};

export const continuingReviewFormSections: FormSection[] = [
  {
    heading: 'Study & Source Approval Information',
    description:
      'These details are pre-filled from your approved submission. Review them and update only if something has changed.',
    items: [
      {
        itemType: 'instruction',
        label:
          'You are completing a <b>continuing review</b> for a study previously approved by Timely IRB. A continuing review does not extend your current approval until the IRB issues a new decision. If your approval has expired, human-subject research activities must remain paused until re-approval.',
      },
      {
        itemType: 'question',
        name: 'ref_name',
        label: 'Reference name for this study:',
        fieldType: 'text',
        placeholder: 'short version of protocol name for easy reference, e.g., ABC study',
        validation: {
          required: true,
          maxLength: 30,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'protocol_title',
        label: 'Full title of the research protocol:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 300,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'sponsor_name',
        label: 'What institution is sponsoring this study?',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
    ],
  },
  {
    heading: 'Current Study Status',
    items: [
      {
        itemType: 'question',
        name: 'cr_study_status',
        label: 'What is the <b>current status</b> of this study?',
        fieldType: 'radio',
        options: [
          'Not yet started - no participants enrolled',
          'Open to enrollment',
          'Closed to enrollment - intervention or follow-up ongoing',
          'Data analysis only',
          'Study complete - closure requested',
        ],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cr_status_explanation',
        label: 'Briefly describe the current state of study activities:',
        fieldType: 'text',
        rows: 3,
        placeholder: 'e.g., enrollment ongoing at one site; follow-up visits continue through 2027',
        validation: {
          required: true,
          maxLength: 2000,
        },
      },
    ],
  },
  {
    heading: 'Enrollment & Progress',
    items: [
      {
        itemType: 'question',
        name: 'cr_enrollment_target',
        label: 'Approved enrollment target (number of participants):',
        fieldType: 'text',
        validation: NUMERIC_VALIDATION,
      },
      {
        itemType: 'question',
        name: 'cr_enrolled_since_last_approval',
        label: 'Participants enrolled <b>since the last IRB approval</b>:',
        fieldType: 'text',
        validation: NUMERIC_VALIDATION,
      },
      {
        itemType: 'question',
        name: 'cr_enrolled_total',
        label: 'Total participants enrolled <b>to date</b>:',
        fieldType: 'text',
        validation: NUMERIC_VALIDATION,
      },
      {
        itemType: 'question',
        name: 'cr_active_participants',
        label: 'Participants currently active (enrolled, in intervention, or in follow-up):',
        fieldType: 'text',
        validation: NUMERIC_VALIDATION,
      },
      {
        itemType: 'question',
        name: 'cr_completed_withdrawn_participants',
        label: 'Participants who completed the study or withdrew:',
        fieldType: 'text',
        validation: NUMERIC_VALIDATION,
      },
    ],
  },
  {
    heading: 'Changes Since Last Approval',
    items: [
      {
        itemType: 'question',
        name: 'cr_any_changes',
        label:
          'Have there been any changes to the study during this reporting period, including changes reviewed and approved through amendments?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cr_change_categories',
        label: 'Which areas changed? Select all that apply:',
        fieldType: 'checkboxes',
        options: [
          'Protocol or study procedures',
          'Consent or recruitment materials',
          'Investigators or study sites',
          'Funding or sponsor',
          'Privacy or data security',
          'Other',
        ],
        dependency: { field: 'cr_any_changes', value: 'Yes' },
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cr_changes_description',
        label:
          'Briefly summarize each amendment, including its number, approval date, and a 1-3 sentence description.',
        fieldType: 'text',
        rows: 4,
        dependency: { field: 'cr_any_changes', value: 'Yes' },
        validation: {
          required: true,
          maxLength: 5000,
        },
      },
      {
        itemType: 'question',
        name: 'cr_no_changes_attestation',
        label: 'Please confirm:',
        fieldType: 'checkboxes',
        options: [
          'I attest that no changes requiring IRB review have been implemented without prior Timely IRB review and approval (e.g., via Protocol Amendments) during this reporting period.',
        ],
        dependency: { field: 'cr_any_changes', value: 'No' },
        validation: { required: true },
      },
    ],
  },
  {
    heading: 'Safety & Compliance',
    description:
      'Report all events since the last IRB approval. Supporting documentation is required for any item marked Yes.',
    items: [
      {
        itemType: 'question',
        name: 'cr_adverse_events',
        label: 'Have any <b>adverse events</b> occurred?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cr_adverse_events_description',
        label: 'Describe each adverse event, its severity, and resolution:',
        fieldType: 'text',
        rows: 4,
        dependency: { field: 'cr_adverse_events', value: 'Yes' },
        validation: { required: true, maxLength: 5000 },
      },
      {
        itemType: 'document',
        name: 'doc_cr_adverse_event_reports',
        allowMultiple: true,
        label: 'Upload adverse event report(s):',
        documentType: '.pdf',
        dependency: { field: 'cr_adverse_events', value: 'Yes' },
        validation: {
          validate: {
            docCheck: (val: any) => Boolean(val) || 'Adverse event report is required',
          },
        },
      },
      {
        itemType: 'question',
        name: 'cr_unanticipated_problems',
        label:
          'Have any <b>unanticipated problems</b> involving risks to participants or others (UPIRTSOs) occurred?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cr_unanticipated_problems_description',
        label: 'Describe each unanticipated problem and the corrective actions taken:',
        fieldType: 'text',
        rows: 4,
        dependency: { field: 'cr_unanticipated_problems', value: 'Yes' },
        validation: { required: true, maxLength: 5000 },
      },
      {
        itemType: 'question',
        name: 'cr_protocol_deviations',
        label: 'Have any <b>protocol deviations</b> occurred?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cr_protocol_deviations_description',
        label: 'Describe each deviation and its impact on participants or data integrity:',
        fieldType: 'text',
        rows: 4,
        dependency: { field: 'cr_protocol_deviations', value: 'Yes' },
        validation: { required: true, maxLength: 5000 },
      },
      {
        itemType: 'document',
        name: 'doc_cr_deviation_reports',
        allowMultiple: true,
        label: 'Upload deviation report(s):',
        documentType: '.pdf',
        dependency: { field: 'cr_protocol_deviations', value: 'Yes' },
        validation: {
          validate: {
            docCheck: (val: any) => Boolean(val) || 'Deviation report is required',
          },
        },
      },
      {
        itemType: 'question',
        name: 'cr_noncompliance',
        label: 'Have any instances of <b>noncompliance</b> occurred?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cr_noncompliance_description',
        label: 'Describe each instance of noncompliance and the corrective actions taken:',
        fieldType: 'text',
        rows: 4,
        dependency: { field: 'cr_noncompliance', value: 'Yes' },
        validation: { required: true, maxLength: 5000 },
      },
      {
        itemType: 'question',
        name: 'cr_participant_complaints',
        label: 'Have any <b>participant complaints</b> been received?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cr_participant_complaints_description',
        label: 'Summarize the complaints and how they were addressed:',
        fieldType: 'text',
        rows: 4,
        dependency: { field: 'cr_participant_complaints', value: 'Yes' },
        validation: { required: true, maxLength: 5000 },
      },
      {
        itemType: 'question',
        name: 'cr_dsmb_review',
        label:
          'Has a Data Safety Monitoring Board (DSMB) or other safety monitoring entity issued any reports since the last approval?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'document',
        name: 'doc_cr_dsmb_reports',
        allowMultiple: true,
        label: 'Upload DSMB / safety monitoring report(s):',
        documentType: '.pdf',
        dependency: { field: 'cr_dsmb_review', value: 'Yes' },
        validation: {
          validate: {
            docCheck: (val: any) => Boolean(val) || 'Safety monitoring report is required',
          },
        },
      },
    ],
  },
  {
    heading: 'Risk-Benefit Assessment & Literature',
    items: [
      {
        itemType: 'question',
        name: 'cr_new_risks',
        label:
          'Has any <b>new risk information</b> emerged (from this study, the literature, or other sources)?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cr_new_risks_description',
        label: 'Describe the new risk information:',
        fieldType: 'text',
        rows: 3,
        dependency: { field: 'cr_new_risks', value: 'Yes' },
        validation: { required: true, maxLength: 5000 },
      },
      {
        itemType: 'question',
        name: 'cr_new_benefits',
        label: 'Has any <b>new benefit information</b> emerged?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cr_new_benefits_description',
        label: 'Describe the new benefit information:',
        fieldType: 'text',
        rows: 3,
        dependency: { field: 'cr_new_benefits', value: 'Yes' },
        validation: { required: true, maxLength: 5000 },
      },
      {
        itemType: 'question',
        name: 'cr_relevant_publications',
        label:
          'Are there relevant <b>publications or safety reports</b> the IRB should consider with this renewal?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cr_relevant_publications_description',
        label: 'List the publications/reports (citations or links):',
        fieldType: 'text',
        rows: 3,
        dependency: { field: 'cr_relevant_publications', value: 'Yes' },
        validation: { required: true, maxLength: 5000 },
      },
      {
        itemType: 'question',
        name: 'cr_risk_category_changed',
        label:
          'In your assessment, has the overall <b>risk category</b> of the study changed since the last approval?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cr_risk_category_changed_description',
        label: 'Explain how and why the risk category changed:',
        fieldType: 'text',
        rows: 3,
        dependency: { field: 'cr_risk_category_changed', value: 'Yes' },
        validation: { required: true, maxLength: 5000 },
      },
    ],
  },
  {
    heading: 'Continuing Review Documents',
    items: [
      {
        itemType: 'document',
        name: 'doc_cr_progress_report',
        allowMultiple: true,
        label:
          'Upload a <b>progress report</b> summarizing study conduct since the last approval:',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) => Boolean(val) || 'Progress report is required',
          },
        },
      },
      {
        itemType: 'document',
        name: 'doc_protocol',
        label: 'Upload the <b>current protocol</b> (clean copy):',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) => Boolean(val) || 'Current protocol is required',
          },
        },
      },
      {
        itemType: 'question',
        name: 'cr_uses_consent_forms',
        label: 'Does this study use informed consent forms?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'document',
        name: 'doc_consent_form',
        allowMultiple: true,
        label: 'Upload the <b>current consent form(s)</b>:',
        documentType: '.pdf',
        dependency: { field: 'cr_uses_consent_forms', value: 'Yes' },
        validation: {
          validate: {
            docCheck: (val: any) => Boolean(val) || 'Current consent form is required',
          },
        },
      },
      {
        itemType: 'question',
        name: 'cr_uses_recruitment_materials',
        label: 'Is the study actively using recruitment materials?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'document',
        name: 'doc_recruitment_materials',
        allowMultiple: true,
        label: 'Upload the current recruitment material(s):',
        documentType: '.pdf',
        dependency: { field: 'cr_uses_recruitment_materials', value: 'Yes' },
        validation: {
          validate: {
            docCheck: (val: any) => Boolean(val) || 'Recruitment materials are required',
          },
        },
      },
      {
        itemType: 'question',
        name: 'cr_has_additional_documents',
        label: 'Do you have additional supporting documents to upload?',
        fieldType: 'radio',
        options: YES_NO,
        validation: { required: true },
      },
      {
        itemType: 'document',
        name: 'doc_additional_files',
        allowMultiple: true,
        label: 'Upload additional supporting document(s):',
        documentType: '.pdf',
        dependency: { field: 'cr_has_additional_documents', value: 'Yes' },
        validation: {
          validate: {
            docCheck: (val: any) => Boolean(val) || 'Document is required',
          },
        },
      },
    ],
  },
  {
    heading: 'Billing Contact Information',
    description: 'An invoice for the continuing review fee is issued when this form is submitted. The invoice is issued to the billing entity named below, or to the study sponsor if no billing entity is provided.',
    items: [
      {
        itemType: 'question',
        name: 'billing_contact_company',
        label: 'Billing entity <b>legal name</b> (optional) <br><small>The organization the invoice should be issued to (e.g., a CRO or site). Leave blank to invoice the study sponsor.</small>',
        fieldType: 'text',
        placeholder: 'Leave blank to invoice the study sponsor',
        validation: {
          required: false,
          maxLength: 200,
        },
      },
      {
        itemType: 'question',
        name: 'billing_contact_first_name',
        label: 'Billing contact <b>first name</b>:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
        },
      },
      {
        itemType: 'question',
        name: 'billing_contact_last_name',
        label: 'Billing contact <b>last name</b>:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
        },
      },
      {
        itemType: 'question',
        name: 'invoice_email',
        label: 'Email address for the <b>invoice</b>:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 254,
          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        },
      },
      {
        itemType: 'question',
        name: 'billing_contact_phone',
        label: 'Billing contact phone number (optional):',
        fieldType: 'text',
        validation: {
          required: false,
          minLength: 10,
          maxLength: 10,
          pattern: /^[0-9()+-\s]+$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_first_name',
        label: 'Principal Investigator <b>first name</b>:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
        },
      },
      {
        itemType: 'question',
        name: 'pi_last_name',
        label: 'Principal Investigator <b>last name</b>:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
        },
      },
      {
        itemType: 'question',
        name: 'pi_email',
        label:
          'Confirm the <b>Principal Investigator email</b> for attestation and approval notifications:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 254,
          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        },
      },
    ],
  },
  {
    heading: 'Submitter Attestation',
    items: [
      {
        itemType: 'question',
        name: 'cr_submitter_attestation',
        label: 'Attestation:',
        fieldType: 'checkboxes',
        options: [
          'I certify that the information provided in this continuing review is accurate and complete, and that the study has been and will continue to be conducted in accordance with the IRB-approved protocol.',
        ],
        validation: { required: true },
      },
    ],
  },
];
